import React, { useLayoutEffect, useRef, useState } from "react";

// Hoja tamaño carta/A4 a 96 dpi.
export const PAGE_W = 794;
export const PAGE_H = 1123;
const PAD_X = 72;
const TOP = 88;
const BOTTOM = 84;
const CONTENT_H = PAGE_H - TOP - BOTTOM;

const NAVY = "#1f3b5b";

// **negritas** dentro del texto.
export const Rich = ({ text }) =>
  (text || "").split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? <b key={i}>{part.slice(2, -2)}</b> : <React.Fragment key={i}>{part}</React.Fragment>
  );

const para = { fontSize: 12, lineHeight: 1.5, color: "#1a1a1a", textAlign: "justify", margin: 0 };

// Convierte el contrato en bloques que se reparten entre páginas.
const buildBlocks = (c) => {
  const out = [
    {
      key: "head",
      el: (
        <div style={{ textAlign: "center", paddingTop: 18, paddingBottom: 22 }}>
          <div style={{ fontSize: 27, fontWeight: 800, color: NAVY, lineHeight: 1.05 }}>{c.brand}</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#111", lineHeight: 1.32, margin: "2px 20px 0" }}>{c.title}</div>
          {c.subtitle && <div style={{ fontSize: 11.6, color: "#6b7280", marginTop: 14, letterSpacing: 0.2 }}>{c.subtitle}</div>}
        </div>
      ),
    },
  ];
  if (c.intro) out.push({ key: "intro", el: <p style={{ ...para, paddingBottom: 26 }}><Rich text={c.intro} /></p> });
  (c.clauses || []).forEach((cl) => {
    out.push({
      key: `t-${cl.id}`,
      keepNext: true,
      el: <h3 style={{ fontSize: 13.2, fontWeight: 800, color: NAVY, margin: "6px 0 5px" }}>{cl.title}</h3>,
    });
    if (cl.body) out.push({ key: `b-${cl.id}`, el: <p style={{ ...para, paddingBottom: 6 }}><Rich text={cl.body} /></p> });
    if (cl.table && cl.table.length) {
      cl.table.forEach((row, ri) =>
        out.push({
          key: `r-${cl.id}-${ri}`,
          el: (
            <div style={{ display: "grid", gridTemplateColumns: "17% 26% 57%", margin: "0 34px", fontSize: 10.4,
              border: "1px solid #e5e7eb", borderTop: ri === 0 ? "1px solid #e5e7eb" : "none",
              background: ri === 0 ? NAVY : "#fff", color: ri === 0 ? "#fff" : "#1f2937", fontWeight: ri === 0 ? 700 : 400,
              marginTop: ri === 0 ? 6 : 0, marginBottom: ri === cl.table.length - 1 ? 14 : 0 }}>
              {row.map((cell, ci) => (
                <div key={ci} style={{ padding: "7px 10px", borderLeft: ci ? `1px solid ${ri === 0 ? "#2d4f75" : "#e5e7eb"}` : "none" }}>{cell}</div>
              ))}
            </div>
          ),
        })
      );
    }
  });
  if ((c.signers || []).length) {
    out.push({
      key: "signs",
      el: (
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(c.signers.length, 2)}, 1fr)`, gap: "40px 30px", padding: "30px 10px 14px" }}>
          {c.signers.map((s) => (
            <div key={s.id} style={{ textAlign: "center", color: "#111" }}>
              <div style={{ fontSize: 12.4, fontWeight: 800 }}>{s.name}</div>
              <div style={{ borderBottom: "1px solid #111", margin: "54px 34px 8px" }} />
              <div style={{ fontSize: 12 }}>{s.role}</div>
              <div style={{ fontSize: 14, marginTop: 16 }}>Fecha: ________________________</div>
            </div>
          ))}
        </div>
      ),
    });
  }
  if (c.note) out.push({ key: "note", el: <p style={{ fontSize: 10.2, lineHeight: 1.5, color: "#4b5563", margin: "8px 0 0" }}>{c.note}</p> });
  return out;
};

const Page = ({ c, n, children, pageRef }) => (
  <div ref={pageRef} className="contract-page" style={{ width: PAGE_W, height: PAGE_H, background: "#fff", position: "relative",
    fontFamily: "Helvetica, Arial, sans-serif", boxShadow: "0 6px 24px rgba(0,0,0,.12)" }}>
    <div style={{ position: "absolute", left: PAD_X - 12, right: PAD_X - 12, top: 40, display: "flex", justifyContent: "space-between",
      alignItems: "center", borderBottom: "1.5px solid #d1d5db", paddingBottom: 8 }}>
      <span style={{ fontSize: 12.5, fontWeight: 800, color: NAVY }}>{c.headerLeft}</span>
      <span style={{ fontSize: 11, color: "#6b7280" }}>{c.headerRight}</span>
    </div>
    <div style={{ position: "absolute", left: PAD_X, right: PAD_X, top: TOP, height: CONTENT_H, overflow: "hidden" }}>{children}</div>
    <div style={{ position: "absolute", left: PAD_X - 12, right: PAD_X - 12, bottom: 34, display: "flex", justifyContent: "space-between",
      borderTop: "1.5px solid #d1d5db", paddingTop: 8, fontSize: 11, color: "#6b7280" }}>
      <span>{c.footerLeft}</span>
      <span>Página {n}</span>
    </div>
  </div>
);

// Mide cada bloque y lo acomoda en páginas (el título de cláusula nunca queda solo al final).
export const ContractPages = ({ contract, pageRefs, scale = 1 }) => {
  const blocks = buildBlocks(contract);
  const measureRef = useRef(null);
  const [pages, setPages] = useState([blocks.map((_, i) => i)]);
  const sig = JSON.stringify(contract);

  useLayoutEffect(() => {
    const kids = measureRef.current ? [...measureRef.current.children] : [];
    const h = kids.map((k) => k.getBoundingClientRect().height);
    const res = [[]];
    let used = 0;
    h.forEach((bh, i) => {
      const need = blocks[i].keepNext && h[i + 1] ? bh + Math.min(h[i + 1], 60) : bh;
      if (used + need > CONTENT_H && res[res.length - 1].length) {
        res.push([]);
        used = 0;
      }
      res[res.length - 1].push(i);
      used += bh;
    });
    setPages(res);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  const totalH = pages.length * (PAGE_H + 24) - 24;
  return (
    <div style={{ width: PAGE_W * scale, height: totalH * scale, position: "relative" }}>
      <div ref={measureRef} aria-hidden="true" style={{ position: "fixed", visibility: "hidden", width: PAGE_W - PAD_X * 2, left: -99999, top: 0,
        fontFamily: "Helvetica, Arial, sans-serif" }}>
        {blocks.map((b) => <div key={b.key} style={{ display: "flow-root" }}>{b.el}</div>)}
      </div>
      <div style={{ transform: scale === 1 ? "none" : `scale(${scale})`, transformOrigin: "top left", width: PAGE_W, position: "absolute", top: 0, left: 0 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          {pages.map((idx, pi) => (
            <Page key={pi} c={contract} n={pi + 1} pageRef={pageRefs ? (el) => (pageRefs.current[pi] = el) : undefined}>
              {idx.map((i) => <div key={blocks[i].key} style={{ display: "flow-root" }}>{blocks[i].el}</div>)}
            </Page>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ContractPages;
